import React from 'react';
import type { BuilderController } from './useBuilder';
import { cn } from '../../../src/lib/utils';
const STATUS_TONE: [RegExp, string][] = [[/approved|resolved|shortlisted|ic scheduled|pass/i, 'bg-[#E7F6EC] text-[#1E7A46]'], [/escalat|failed|declined|below|remediation|credit/i, 'bg-[#FDECEC] text-[#B42318]']];
const toneFor = (status: string) => STATUS_TONE.find(([re]) => re.test(status))?.[1] ?? 'bg-[#FDF3E7] text-[#9A5B13]';

/** Live-looking preview of the generated application. Fictional data. */
export function AppPreview({
  builder
}: {
  builder: BuilderController;
}) {
  const {
    template,
    nodes,
    building
  } = builder;
  const preview = template.preview;
  const total = template.nodes.length || 1;
  const progress = Math.min(nodes.length / total, 1);
  const modules = nodes.slice(0, 7).map(n => n.label);
  if (template.id === 'blank' && nodes.length === 0) {
    return <div className="flex h-full min-h-[260px] flex-col items-center justify-center rounded-xl border border-dashed border-night-line p-6 text-center" data-id="element-79" data-mp="34gbsndefrpjx">
        <p className="text-sm font-semibold text-night-text" data-id="element-80" data-mp="1m1bcj62re961">No interface yet</p>
        <p className="mt-1 max-w-[240px] text-[12px] text-night-muted" data-id="element-81" data-mp="1qzcypzvheolq">The application preview appears as you describe the process or add steps.</p>
      </div>;
  }
  const showRows = template.id === 'blank' ? 0 : Math.ceil(preview.rows.length * progress);
  return <div className="overflow-hidden rounded-xl border border-hairline bg-panel text-text-primary" data-id="element-82" data-mp="24z4h0ct16r5j">
      <div className="flex items-center justify-between gap-2 border-b border-hairline bg-surface px-3 py-2" data-id="element-83" data-mp="29wmglcl5ru0v">
        <div className="min-w-0" data-id="element-84" data-mp="2ep76w44uvozn">
          <p className="truncate text-[12px] font-semibold" data-id="element-85" data-mp="2eoxop2q1ym4i">{template.id === 'blank' ? 'Custom application' : preview.title}</p>
          <p className="truncate text-[10px] text-text-tertiary" data-id="element-86" data-mp="2nou74mlumor4">{template.id === 'blank' ? `${nodes.length} steps configured` : preview.subtitle}</p>
        </div>
        <span className="shrink-0 rounded bg-[#FDF3E7] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.06em] text-[#9A5B13]" data-id="element-87" data-mp="2so6wpf43u9xe">Illustrative</span>
      </div>
      <div className="flex" data-id="element-88" data-mp="nnmbpdf51qun">
        <ul className="hidden w-[92px] shrink-0 space-y-0.5 border-r border-hairline-soft bg-surface p-1.5 sm:block" aria-label="Application modules" data-id="element-89" data-mp="noe8usodvwn1">
          {modules.map((label, index) => <li key={`${label}-${index}`} className={cn('truncate rounded px-1.5 py-1 text-[10px]', index === 0 ? 'bg-violet-soft font-semibold text-lime' : 'text-text-secondary')} data-id="element-90" data-mp="xjfwtl6xaso1" data-mpi={index}>
              {label}
            </li>)}
        </ul>
        <div className="min-w-0 flex-1 p-2.5" data-id="element-91" data-mp="1la35w9skroc6">
          {preview.kpis.length ? <div className="grid grid-cols-3 gap-1.5" data-id="element-92" data-mp="1gb8pfei2ybv8">
              {preview.kpis.map(kpi => <div key={kpi.label} className={cn('rounded-lg border border-hairline-soft p-2 transition-opacity', progress < 0.4 && 'opacity-40')} data-id="element-93" data-mp="3n1op8v6bd9lj">
                  <p className="text-[15px] font-semibold tabular-nums leading-none" data-id="element-94" data-mp="3i3wulteab9dn">{kpi.value}</p>
                  <p className="mt-1 truncate text-[9px] text-text-tertiary" data-id="element-95" data-mp="1xlvsgyvuwg5i">{kpi.label}</p>
                </div>)}
            </div> : null}
          {preview.columns.length ? <table className="mt-2.5 w-full table-fixed text-left text-[10px]" data-id="element-96" data-mp="1nu87ngtt1xd0">
              <thead data-id="element-97" data-mp="1sssywqr2x6pb">
                <tr className="text-text-tertiary" data-id="element-98" data-mp="1ss9nlsvxpgk9">
                  {preview.columns.map(col => <th key={col} scope="col" className="truncate pb-1 font-medium" data-id="element-99" data-mp="1esryfceq81ad">
                      {col}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-100" data-mp="14xq6k8t2gl43">
                {preview.rows.slice(0, showRows).map(row => <tr key={row[0]} className="border-t border-hairline-soft" data-id="element-101" data-mp="19vi9ryz5vtnl">
                    {row.map((cell, i) => i === row.length - 1 ? <td key={i} className="py-1.5" data-id="element-102" data-mp="3499imw9o29hz">
                          <span className={cn('inline-block max-w-full truncate rounded px-1 py-0.5 text-[9px] font-medium', toneFor(cell))} data-id="element-103" data-mp="346n9is74nlxn">{cell}</span>
                        </td> : <td key={i} className={cn('truncate py-1.5 pr-1', i === 0 && 'font-medium')} data-id="element-104" data-mp="v63dg9u6mf55">
                          {cell}
                        </td>)}
                  </tr>)}
              </tbody>
            </table> : null}
          {building || showRows < preview.rows.length ? <div className="mt-2 space-y-1.5" aria-hidden="true" data-id="element-105" data-mp="gfw4ckq2e8h4">
              <div className="h-3 animate-pulse rounded bg-elevated motion-reduce:animate-none" data-id="element-106" data-mp="cgnc5kaoaino" />
              <div className="h-3 w-4/5 animate-pulse rounded bg-elevated motion-reduce:animate-none" data-id="element-107" data-mp="7hk5xwiyi6e4" />
            </div> : null}
          {template.id === 'blank' ? <p className="mt-2 text-[10px] text-text-tertiary" data-id="element-108" data-mp="3tn7bouspg45f">Screens for your custom steps are generated as you refine the workflow.</p> : null}
        </div>
      </div>
    </div>;
}